import React, { useEffect, useRef, useState } from 'react';
import { authHeaders } from '../utils/authHeaders';
import '../styles/procurement-admin.css';

const API_BASE = process.env.REACT_APP_API_BASE;
const EMPTY_FILTERS = { from: '', to: '', crop_id: '', variety_id: '', grade_id: '', aggregator_id: '', seller_kind: '' };
const PAGE_SIZES = [50, 100, 200, 500];

async function failure(response, fallback) {
  try {
    const body = await response.json();
    if (typeof body.message === 'string') return body.message;
  } catch (_) { /* Non-JSON failures use a controlled message. */ }
  return fallback;
}

function downloadFilename(header) {
  const match = /filename="([^"]+)"|filename=([^;\s]+)/i.exec(header || '');
  const name = match && (match[1] || match[2]);
  const unsafe = name && (/[\\/]/.test(name) || Array.from(name).some(character => character.charCodeAt(0) < 32));
  return name && !unsafe ? name : 'procurement_register.csv';
}

export default function ProcurementRegister() {
  return localStorage.getItem('role') === 'fpo_admin'
    ? <RegisterReport />
    : <p role="alert">FPO Admin Procurement Register access required.</p>;
}

function RegisterReport() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState(null);
  const [options, setOptions] = useState({ crops: [], varieties: [], grades: [], aggregators: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const downloadRunning = useRef(false);
  const [receiptError, setReceiptError] = useState('');
  const [receiptLoading, setReceiptLoading] = useState('');
  const [preview, setPreview] = useState(null);
  const previewUrl = useRef(null);
  const mounted = useRef(true);
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
  const query = params.toString();
  const endpoint = `${API_BASE}/procurement/v1/admin/procurement-register`;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
      previewUrl.current = null;
    };
  }, []);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    async function load() {
      try {
        const response = await fetch(`${endpoint}?${query}`, { headers: authHeaders() });
        if (!response.ok) throw new Error(await failure(response, 'Unable to load Procurement Register.'));
        const result = await response.json();
        if (current) {
          setData(result);
          setOptions(result.filter_options);
        }
      } catch (err) {
        if (current) setError(err.message === 'Failed to fetch' ? 'Unable to load Procurement Register. Please retry.' : err.message);
      } finally {
        if (current) setLoading(false);
      }
    }
    load();
    return () => { current = false; };
  }, [endpoint, query, refresh]);

  function changeFilter(key, value) {
    setPage(1);
    setFilters(previous => {
      const next = { ...previous, [key]: value };
      if (key === 'crop_id' && value) {
        if (!options.varieties.some(x => String(x.id) === next.variety_id && String(x.crop_id) === value)) next.variety_id = '';
        if (!options.grades.some(x => String(x.id) === next.grade_id && String(x.crop_id) === value)) next.grade_id = '';
      }
      return next;
    });
  }

  async function download() {
    if (downloadRunning.current) return;
    downloadRunning.current = true;
    setDownloading(true);
    setDownloadError('');
    let url;
    let anchor;
    try {
      const response = await fetch(`${endpoint}?${query}&export=csv`, { headers: authHeaders() });
      if (!response.ok) throw new Error(await failure(response, 'Unable to download CSV. Please retry.'));
      const blob = await response.blob();
      url = URL.createObjectURL(blob);
      anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = downloadFilename(response.headers.get('Content-Disposition'));
      document.body.appendChild(anchor);
      anchor.click();
    } catch (err) {
      setDownloadError(err.message === 'Failed to fetch' ? 'Unable to download CSV. Please retry.' : err.message);
    } finally {
      if (anchor) anchor.remove();
      if (url) URL.revokeObjectURL(url);
      downloadRunning.current = false;
      setDownloading(false);
    }
  }

  function receiptEndpoint(id) {
    return `${API_BASE}/procurement/v1/admin/procurements/${encodeURIComponent(id)}/receipt-pdf`;
  }

  async function openReceipt(id) {
    if (receiptLoading) return;
    setReceiptLoading(`view:${id}`);
    setReceiptError('');
    try {
      const response = await fetch(receiptEndpoint(id), { headers: authHeaders() });
      if (!response.ok) throw new Error(await failure(response, 'Unable to open Provisional Procurement Receipt.'));
      const url = URL.createObjectURL(await response.blob());
      if (!mounted.current) { URL.revokeObjectURL(url); return; }
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
      previewUrl.current = url;
      setPreview({ id, url });
    } catch (err) {
      if (mounted.current) setReceiptError(err.message === 'Failed to fetch' ? 'Unable to open Provisional Procurement Receipt. Please retry.' : err.message);
    } finally {
      if (mounted.current) setReceiptLoading('');
    }
  }

  async function downloadReceipt(id) {
    if (receiptLoading) return;
    setReceiptLoading(`download:${id}`);
    setReceiptError('');
    let url;
    let anchor;
    try {
      const response = await fetch(receiptEndpoint(id), { headers: authHeaders() });
      if (!response.ok) throw new Error(await failure(response, 'Unable to download Provisional Procurement Receipt.'));
      url = URL.createObjectURL(await response.blob());
      anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `provisional_procurement_receipt_${id}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
    } catch (err) {
      if (mounted.current) setReceiptError(err.message === 'Failed to fetch' ? 'Unable to download Provisional Procurement Receipt. Please retry.' : err.message);
    } finally {
      if (anchor) anchor.remove();
      if (url) URL.revokeObjectURL(url);
      if (mounted.current) setReceiptLoading('');
    }
  }

  function closePreview() {
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = null;
    setPreview(null);
  }

  const visibleOptions = list => list.filter(x => !filters.crop_id || String(x.crop_id) === filters.crop_id);
  function select(label, key, list) {
    return <label>{label}<select value={filters[key]} onChange={e => changeFilter(key, e.target.value)}>
      <option value="">All</option>{list.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
    </select></label>;
  }
  const currentPage = data?.page ?? page;
  const currentSize = data?.page_size ?? pageSize;
  const total = data?.total_rows ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / currentSize));
  return <section className="proc-admin proc-admin--register" aria-label="Procurement Register">
    <div className="register-heading"><h3>Procurement Register</h3>
      <button type="button" onClick={download} disabled={downloading || loading || !!error}>{downloading ? 'Downloading…' : 'Download CSV'}</button>
    </div>
    <p>Locked procurements only. Procurement Date is the physical LOCK date. Date filters use India calendar days. Provisional Value is the field value, not final settlement.</p>
    <div className="register-filters">
      {['from', 'to'].map(key => <label key={key}>{key === 'from' ? 'From' : 'To'}<input type="date" value={filters[key]} onChange={e => changeFilter(key, e.target.value)} /></label>)}
      {select('Crop', 'crop_id', options.crops)}
      {select('Variety', 'variety_id', visibleOptions(options.varieties))}
      {select('Field Grade', 'grade_id', visibleOptions(options.grades))}
      {select('Aggregator', 'aggregator_id', options.aggregators)}
      <label>Seller Type<select value={filters.seller_kind} onChange={e => changeFilter('seller_kind', e.target.value)}>
        <option value="">All</option><option value="FARMER">Farmer</option><option value="WALK_IN">Walk-in</option>
      </select></label>
    </div>
    <button type="button" onClick={() => setRefresh(x => x + 1)} disabled={loading}>Refresh</button>
    {loading && <p role="status">Loading Procurement Register…</p>}
    {error && <p role="alert">{error}</p>}
    {downloadError && <p role="alert">{downloadError}</p>}
    {receiptError && <p role="alert">{receiptError}</p>}
    {!loading && !error && data && <>
      <div className="register-table-toolbar" role="group" aria-label="Table results and page size">
        <span>Total: {total}</span>
        <label>Rows per page<select value={currentSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>{PAGE_SIZES.map(size => <option key={size}>{size}</option>)}</select></label>
      </div>
      <div className="register-table-scroll" role="region" aria-label="Locked Procurement table" tabIndex={0}>
        <table><thead><tr>{data.columns.map(column => <th scope="col" key={column.key}>{column.label}</th>)}<th scope="col">Actions</th></tr></thead>
          <tbody>{data.rows.map(row => <tr key={row.procurement_reference}>{data.columns.map(column => <td key={column.key}>
            {column.key === 'seller_type' ? ({ FARMER: 'Farmer', WALK_IN: 'Walk-in' }[row[column.key]] ?? row[column.key]) : (row[column.key] ?? '')}
          </td>)}<td className="register-receipt-actions">{row.has_provisional_receipt === true && <>
            <button type="button" aria-label={`View receipt ${row.procurement_reference}`} title="View receipt" disabled={!!receiptLoading} onClick={() => openReceipt(row.procurement_reference)}>{receiptLoading === `view:${row.procurement_reference}` ? 'Opening…' : '👁 View'}</button>
            <button type="button" aria-label={`Download receipt ${row.procurement_reference}`} disabled={!!receiptLoading} onClick={() => downloadReceipt(row.procurement_reference)}>{receiptLoading === `download:${row.procurement_reference}` ? 'Downloading…' : 'Download'}</button>
          </>}</td></tr>)}</tbody>
        </table>
      </div>
      {data.rows.length === 0 && <p role="status">No locked procurements match the selected filters.</p>}
      <div className="register-pagination">
        <button type="button" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>Previous</button>
        <span>Page {currentPage} of {totalPages}</span>
        <button type="button" disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}>Next</button>
      </div>
      {preview && <div className="register-receipt-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) closePreview(); }}>
        <section className="register-receipt-dialog" role="dialog" aria-modal="true" aria-label="Provisional Procurement Receipt">
          <div className="register-receipt-dialog-heading"><h3>Provisional Procurement Receipt</h3><button type="button" onClick={closePreview}>Close</button></div>
          <iframe title="Receipt PDF preview" src={preview.url} />
        </section>
      </div>}
    </>}
  </section>;
}
